import { motion } from "framer-motion";

// Two heavy first-class CHAMPAGNE / GOLD curtain panels that sweep apart
export default function SuiteDoors({ opening }) {
  return (
    <div className="fixed inset-0 pointer-events-none z-30 overflow-hidden">
      {/* Soft golden glow that intensifies as curtains part */}
      <motion.div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(255,231,160,0.45) 0%, rgba(212,175,55,0.12) 35%, transparent 70%)",
        }}
        initial={{ opacity: 0 }}
        animate={opening ? { opacity: [0, 0.9, 0] } : { opacity: 0 }}
        transition={{ duration: 2.4, delay: 0.3, times: [0, 0.45, 1] }}
      />

      {/* Top valance / curtain rod */}
      <motion.div
        className="absolute top-0 left-0 right-0 z-40"
        style={{ height: 52 }}
        initial={{ y: 0 }}
        animate={opening ? { y: -60 } : { y: 0 }}
        transition={{ duration: 1.0, delay: 1.9, ease: [0.6, 0, 0.4, 1] }}
      >
        {/* Champagne velvet pelmet with scalloped bottom */}
        <div
          className="absolute top-2 left-0 right-0"
          style={{
            height: 32,
            background:
              "linear-gradient(180deg, #6E4F1A 0%, #B8860B 25%, #D4AF37 55%, #8a6508 100%)",
            boxShadow:
              "0 6px 18px rgba(0,0,0,0.55), inset 0 -3px 8px rgba(0,0,0,0.4), inset 0 2px 3px rgba(255,231,160,0.35)",
          }}
        />
        {/* Decorative gold beaded fringe under pelmet */}
        <div
          className="absolute left-0 right-0 flex items-center justify-around"
          style={{ top: 32, height: 10 }}
        >
          {Array.from({ length: 22 }).map((_, i) => (
            <motion.div
              key={i}
              style={{
                width: 5,
                height: 8,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at 40% 30%, #FFE7A0 0%, #D4AF37 60%, #8a6508 100%)",
                boxShadow: "0 1px 3px rgba(0,0,0,0.6)",
              }}
              animate={
                opening
                  ? { y: [0, 3, 0], rotate: [0, 6, 0] }
                  : { y: 0, rotate: 0 }
              }
              transition={{
                duration: 0.7,
                delay: 0.3 + i * 0.03,
                repeat: 2,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>
        {/* Gold curtain rod */}
        <div
          className="absolute left-0 right-0"
          style={{
            top: 0,
            height: 6,
            background:
              "linear-gradient(180deg, #8a6508 0%, #FFE7A0 50%, #B8860B 100%)",
            boxShadow:
              "0 3px 8px rgba(0,0,0,0.6), inset 0 1px 1px rgba(255,255,255,0.55)",
          }}
        />
        {/* Rod end finials */}
        <div
          className="absolute"
          style={{
            top: -4,
            left: -8,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 35% 30%, #FFE7A0 0%, #D4AF37 55%, #8a6508 100%)",
            boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
          }}
        />
        <div
          className="absolute"
          style={{
            top: -4,
            right: -8,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 35% 30%, #FFE7A0 0%, #D4AF37 55%, #8a6508 100%)",
            boxShadow: "0 4px 10px rgba(0,0,0,0.5)",
          }}
        />
      </motion.div>

      {/* LEFT CURTAIN - gather + slide */}
      <motion.div
        className="absolute top-0 bottom-0 left-0"
        style={{
          width: "52%",
          height: "100%",
          transformOrigin: "left center",
        }}
        initial={{ x: 0, scaleX: 1, skewY: 0 }}
        animate={
          opening
            ? {
                // Idle sway before parting
                skewY: [0, 0.6, -0.4, 0, 0],
                scaleX: [1, 1, 0.9, 0.55, 0.4],
                x: ["0%", "0%", "-5%", "-60%", "-105%"],
              }
            : { x: 0, scaleX: 1, skewY: 0 }
        }
        transition={{
          duration: 2.6,
          delay: 0.2,
          times: [0, 0.18, 0.32, 0.7, 1],
          ease: [0.6, 0, 0.3, 1],
        }}
        data-testid="suite-door-left"
      >
        <CurtainPanel side="left" opening={opening} />
      </motion.div>

      {/* RIGHT CURTAIN - gather + slide */}
      <motion.div
        className="absolute top-0 bottom-0 right-0"
        style={{
          width: "52%",
          height: "100%",
          transformOrigin: "right center",
        }}
        initial={{ x: 0, scaleX: 1, skewY: 0 }}
        animate={
          opening
            ? {
                skewY: [0, -0.6, 0.4, 0, 0],
                scaleX: [1, 1, 0.9, 0.55, 0.4],
                x: ["0%", "0%", "5%", "60%", "105%"],
              }
            : { x: 0, scaleX: 1, skewY: 0 }
        }
        transition={{
          duration: 2.6,
          delay: 0.2,
          times: [0, 0.18, 0.32, 0.7, 1],
          ease: [0.6, 0, 0.3, 1],
        }}
        data-testid="suite-door-right"
      >
        <CurtainPanel side="right" opening={opening} />
      </motion.div>

      {/* Warm gold light leak as curtains part */}
      <motion.div
        className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
        style={{
          width: 2,
          background:
            "linear-gradient(180deg, transparent 0%, #FFE7A0 50%, transparent 100%)",
          boxShadow:
            "0 0 80px 24px rgba(255, 231, 160, 0.75), 0 0 140px 60px rgba(212,175,55,0.5)",
        }}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={
          opening
            ? { opacity: [0, 1, 1, 0], scaleX: [0, 1, 5, 12] }
            : { opacity: 0 }
        }
        transition={{ duration: 2.6, delay: 0.4, times: [0, 0.2, 0.7, 1] }}
      />

      {/* Sparkle particles drifting in from the gap */}
      {opening &&
        Array.from({ length: 14 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute z-20 pointer-events-none"
            style={{
              left: "50%",
              top: `${10 + i * 6}%`,
              width: 3 + (i % 3),
              height: 3 + (i % 3),
              borderRadius: "50%",
              background: "#FFE7A0",
              boxShadow: "0 0 8px rgba(255, 231, 160, 0.9)",
            }}
            initial={{ opacity: 0, x: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0],
              x: i % 2 === 0 ? [0, -80 - i * 8] : [0, 80 + i * 8],
              y: [0, -20 - (i % 5) * 6],
              scale: [0, 1.2, 0],
            }}
            transition={{
              duration: 2.0,
              delay: 1.0 + i * 0.05,
              ease: "easeOut",
            }}
          />
        ))}
    </div>
  );
}

function CurtainPanel({ side, opening }) {
  // Vertical pleats via repeating linear gradient - CHAMPAGNE / GOLD tones
  const fabric = `
    repeating-linear-gradient(
      90deg,
      rgba(80,55,15,0.55) 0px,
      rgba(120,90,30,0.15) 6px,
      rgba(255,235,180,0.18) 14px,
      rgba(120,90,30,0.15) 22px,
      rgba(80,55,15,0.55) 28px
    ),
    linear-gradient(
      180deg,
      #C8A24A 0%,
      #E6CB7A 25%,
      #F2D98B 50%,
      #C9A24A 80%,
      #8A6B22 100%
    )
  `;

  const edgeShadowSide = side === "left" ? "right" : "left";
  const innerEdge = side === "left" ? "right" : "left";

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Main fabric body with pleats */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: fabric,
          boxShadow:
            side === "left"
              ? "inset -22px 0 50px rgba(80,55,15,0.65), inset 10px 0 14px rgba(120,80,20,0.3)"
              : "inset 22px 0 50px rgba(80,55,15,0.65), inset -10px 0 14px rgba(120,80,20,0.3)",
        }}
      />

      {/* Soft vertical highlights to enhance pleat sheen */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(
            90deg,
            transparent 0px,
            transparent 10px,
            rgba(255,243,200,0.18) 13px,
            transparent 16px,
            transparent 28px
          )`,
        }}
      />

      {/* Diagonal shimmer sweep that animates across the fabric */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(115deg, transparent 35%, rgba(255,243,200,0.35) 50%, transparent 65%)",
          mixBlendMode: "screen",
        }}
        initial={{ x: side === "left" ? "-100%" : "100%" }}
        animate={
          opening
            ? { x: side === "left" ? ["-100%", "120%"] : ["100%", "-120%"] }
            : { x: side === "left" ? "-100%" : "100%" }
        }
        transition={{ duration: 1.2, delay: 0.1, ease: "easeInOut" }}
      />

      {/* Inner edge: gathered darker shadow */}
      <div
        className="absolute top-0 bottom-0"
        style={{
          [edgeShadowSide]: 0,
          width: 28,
          background:
            side === "left"
              ? "linear-gradient(270deg, rgba(60,40,10,0.65) 0%, rgba(60,40,10,0.18) 60%, transparent 100%)"
              : "linear-gradient(90deg, rgba(60,40,10,0.65) 0%, rgba(60,40,10,0.18) 60%, transparent 100%)",
        }}
      />

      {/* Gold braided trim along the inner edge */}
      <div
        className="absolute top-12 bottom-16"
        style={{
          [edgeShadowSide]: 7,
          width: 3,
          background:
            "repeating-linear-gradient(180deg, #FFE7A0 0px, #D4AF37 4px, #8a6508 8px, #D4AF37 12px)",
          boxShadow: "0 0 8px rgba(212,175,55,0.7)",
          borderRadius: 2,
        }}
      />

      {/* Gold tassel holdback near middle - swings during opening */}
      <motion.div
        className="absolute"
        style={{
          [edgeShadowSide]: 14,
          top: "55%",
          width: 32,
          height: 70,
          transformOrigin: "top center",
          pointerEvents: "none",
        }}
        animate={
          opening
            ? { rotate: side === "left" ? [0, -10, 8, -4, 2, 0] : [0, 10, -8, 4, -2, 0] }
            : { rotate: 0 }
        }
        transition={{ duration: 1.6, delay: 0.6, ease: "easeInOut" }}
      >
        {/* Cord wrap */}
        <div
          style={{
            width: 32,
            height: 14,
            borderRadius: "50%",
            background:
              "linear-gradient(180deg, #FFE7A0 0%, #D4AF37 50%, #8a6508 100%)",
            boxShadow:
              "0 4px 10px rgba(0,0,0,0.5), inset 0 -2px 4px rgba(0,0,0,0.35)",
          }}
        />
        {/* Tassel head */}
        <div
          style={{
            marginTop: 6,
            marginLeft: 6,
            width: 20,
            height: 22,
            borderRadius: "50% 50% 30% 30% / 40% 40% 60% 60%",
            background:
              "radial-gradient(ellipse at 50% 30%, #FFE7A0 0%, #D4AF37 40%, #8a6508 100%)",
            boxShadow:
              "0 6px 12px rgba(0,0,0,0.55), inset 0 -3px 6px rgba(0,0,0,0.35)",
          }}
        />
        {/* Tassel fringe */}
        <div
          style={{
            marginTop: 2,
            marginLeft: 6,
            width: 20,
            height: 22,
            backgroundImage: `repeating-linear-gradient(
              90deg,
              #D4AF37 0px,
              #B8860B 2px,
              #8a6508 4px
            )`,
            borderRadius: "0 0 50% 50%",
            opacity: 0.95,
          }}
        />
      </motion.div>

      {/* Bottom hem: pooled fabric shadow */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: 40,
          background:
            "linear-gradient(0deg, rgba(80,55,15,0.7) 0%, transparent 100%)",
        }}
      />
    </div>
  );
}
